import { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import PayPalCheckout from "./PayPalCheckout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BookingsTab({ token }) {
  const { t } = useLang();
  const [bookings, setBookings] = useState([]);
  const [payingId, setPayingId] = useState(null);
  const [msg, setMsg] = useState({ type: null, text: "" });

  const load = async () => {
    const r = await axios.get(`${API}/p/${token}/bookings`);
    setBookings(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const onSuccess = () => {
    setMsg({ type: "success", text: t("payment_successful") });
    setPayingId(null);
    load();
    setTimeout(() => setMsg({ type: null, text: "" }), 4000);
  };
  const onError = (err) => {
    if (err === "cancelled") {
      setPayingId(null);
      return;
    }
    setMsg({ type: "error", text: String(err) });
  };

  const Badge = ({ status, confirmed }) => {
    if (status === "paid" && confirmed) {
      return <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 border border-emerald-500/40 text-emerald-400 text-xs font-medium">✓ {t("paid")} · {t("spot_confirmed")}</span>;
    }
    if (status === "cancelled") {
      return <span className="px-2.5 py-1 rounded-full bg-neutral-500/15 border border-neutral-500/40 text-neutral-400 text-xs">✕ {t("payment_cancelled")}</span>;
    }
    if (status === "failed") {
      return <span className="px-2.5 py-1 rounded-full bg-[#e05555]/15 border border-[#e05555]/40 text-[#e05555] text-xs">✕ {t("payment_failed")}</span>;
    }
    return <span className="px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-400 text-xs">⧗ {t("pending_payment")}</span>;
  };

  return (
    <div className="space-y-4" data-testid="portal-bookings-tab">
      {msg.text && (
        <div className={`rounded-xl p-3.5 text-sm ${msg.type === "success" ? "bg-emerald-500/10 border border-emerald-500/40 text-emerald-300" : "bg-[#e05555]/10 border border-[#e05555]/40 text-[#e05555]"}`}>
          {msg.text}
        </div>
      )}
      {bookings.length === 0 ? (
        <div className="rounded-2xl bg-[#161616] border border-[#252525] p-10 text-center text-neutral-500">
          {t("no_bookings")}
        </div>
      ) : (
        bookings.map((b) => {
          const amount = `$${(b.amount_cents / 100).toFixed(2)}`;
          const isPending = b.payment_status === "pending";
          return (
            <div
              key={b.id}
              data-testid={`booking-${b.id}`}
              className="rounded-2xl bg-[#161616] border border-[#252525] p-5"
            >
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="min-w-0 flex-1">
                  <div className="text-white font-medium">{b.service_title}</div>
                  <div className="text-xs text-neutral-500 mt-0.5">
                    {t("booking_date")}: {new Date(b.booking_date).toLocaleDateString()}
                    {b.payment_date && ` · ${t("payment_date")}: ${new Date(b.payment_date).toLocaleDateString()}`}
                  </div>
                </div>
                <div className="font-bebas text-2xl tracking-widest text-[#e8559a]">
                  {amount}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <Badge status={b.payment_status} confirmed={b.spot_confirmed} />
                {isPending && (
                  <button
                    onClick={() => setPayingId(payingId === b.id ? null : b.id)}
                    data-testid={`pay-btn-${b.id}`}
                    className="px-4 py-2 rounded-lg bg-[#e8559a] hover:bg-[#c9407f] text-white font-bebas text-sm tracking-widest transition-colors"
                  >
                    💳 {t("pay_now")}
                  </button>
                )}
              </div>
              {isPending && payingId === b.id && (
                <div className="mt-4 pt-4 border-t border-[#252525]">
                  <PayPalCheckout
                    token={token}
                    booking={b}
                    onSuccess={onSuccess}
                    onError={onError}
                  />
                </div>
              )}
              {isPending && (
                <p className="text-[10px] uppercase tracking-widest text-amber-400/70 mt-3">
                  ⚠ {t("spot_not_confirmed")}
                </p>
              )}
            </div>
          );
        })
      )}
    </div>
  );
}
